import MainLayout from "components/layout/main-layout/MainLayout";
import { useAdvancedValidation } from "hooks/useAdvancedValidation";
import { titles } from "constants/titles";
import React, { useEffect, useState } from "react";
import "./EmergencyPlan.css";
import {
  IconHide,
  IconQuestion,
  IconReveal,
  IconTrash,
} from "assets/SVGs/SVGs";
import InformationModal from "components/layout/information-modal/InformationModal";
import ConfirmationModal from "components/layout/confirmation-modal/ConfirmationModal";
import AddEmergencyContactModal from "components/layout/add-emergency-contact-modal/AddEmergencyContactModal";
import CustomInput from "components/widgets/custom-input/CustomInput";
import CustomButton from "components/widgets/custom-button/CustomButton";
import CustomCheckbox from "components/widgets/custom-checkbox/CustomCheckbox";
import CustomSelectBox from "components/widgets/custom-select-box/CustomSelectBox";
import MessageSnackBar from "components/widgets/message-snackbar/MessageSnackBar";
import alerts from "constants/alerts";
import SubscriberInformationCard from "components/layout/subscriber-information-card/SubscriberInformationCard";
import SubscriberDetailsCard from "components/layout/subscriber-details-card/SubscriberDetailsCard";
import CustomDraggableCards from "components/layout/custom-draggable-cards/CustomDraggableCards";
import { updateEmergencyPlan } from "redux/EmergencyPlan/actions";
import {
  getFirstAndLastName,
  getLanguageId,
  isSessionTimeExpired,
} from "utility";
import { getLanguages, setSelectedPhoneType } from "redux/Common/action";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ChangesSavedModal from "components/layout/changes-saved-modal/ChangesSavedModal";
import { setSessionExpiryModalVisibility } from "redux/auth/actions";

// Constants for repetitive usage



const EmergencyPlan = () => {
  const [popupMode, setPopUpMode] = useState({ mode: "" });
  const [emergencyPlanState, setEmergencyPlanState] = useState({
    isAcceptSignature: false,
  });

  // Initialize validation hook (same as mobile)
  const { onApiResponse, snackbarState, dismissSnackbar } = useAdvancedValidation();
  const [contactList, setContactList] = useState([]);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [emergencyDetailState, setEmergencyDetailState] = useState({
    township: "",
    jurisdiction: "",
    street: "",
    spacialInstruction: "",
    language: "",
    gateInstruction: "",
    lockBoxCode: "",
    isLockBox: false,
    boxLocation: "",
    keyLocation: "",
    lifeLocation: "",
    accessToResidence: "",
    isShowLockBoxCode: false,
    showPasswordIcon: true
  });
  const [infoModalPopup, setInfoModalPopup] = useState();
  const { loading } = useSelector((store) => store?.emergencyData);
  const selectedPhoneType = useSelector(
    (store) => store?.common?.selectedPhoneType
  );
  const { accountHolderData } = useSelector((store) => store?.userDetail);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const emergencyContactList = location?.state?.emergencyContactList;
  const languages = location?.state?.languages;
  const emergencyData = location?.state?.emergencyData;
  const selectedMedicalUser = useSelector(
    (store) => store?.medicalAlertUsers?.selectedMedicalUser
  );
  const medicalUserDetail = location?.state?.medicalUserDetail;

  useEffect(() => {
    getLanguages({
      dispatch,
      userType: "accountowner",
    });
    setContactList(emergencyContactList);
  }, [dispatch, emergencyContactList]);

  useEffect(() => {
    if (emergencyData) {
      const { language, vial_of_life, emergency_access } = emergencyData;
      const l = languages?.filter(
        (item) => item?.language_id === language?.primary_language
      );
      let selectedLanguage = "";
      if (l && l?.length > 0) {
        selectedLanguage = l[0];
      }
      setEmergencyDetailState({
        ...emergencyPlanState,
        township: emergency_access?.township_borough,
        jurisdiction: emergency_access?.jurisdiction_info,
        street: emergency_access?.cross_street,
        spacialInstruction: emergency_access?.special_instruction,
        language: selectedLanguage,
        gateInstruction: emergency_access?.gate_code,
        lockBoxCode: emergency_access?.lock_box_code,
        boxLocation: emergency_access?.lock_box_location,
        keyLocation: emergency_access?.hidden_key,
        lifeLocation: vial_of_life?.location_of_vial_of_life,
        accessToResidence: emergency_access?.access_to_residence,
      });
    }
  }, [emergencyData]);

  const onRemoveContact = () => {
    const indexToDelete = contactList?.findIndex(
      (item) => item?.key === popupMode?.item?.key
    );

    let cloneData = [...contactList];
    if (cloneData[indexToDelete]) {
      cloneData[indexToDelete].is_delete = true;
      setContactList(cloneData);
      setPopUpMode({ mode: "", item: null });
    }
  };

  const onAccept = async () => {
    let timeDifference = await isSessionTimeExpired();

    // Check for minimum contact requirement (same as mobile)
    const nonDefaultContacts = contactList?.filter(item => !item?.isDefaultPhone) || [];
    if (timeDifference) {
      setSessionExpiryModalVisibility(dispatch, true);
      return;
    } else if (emergencyDetailState?.isLockBox && emergencyDetailState?.lockBoxCode?.length < 4) {
      onApiResponse(alerts?.lock_box_error, "error");
      return;
    }
    else if (nonDefaultContacts.length === 0) {
      onApiResponse(alerts?.minimum_one_contact_required, "error");
      return;
    }
    else {
      const indexOfDefaultContact = contactList?.findIndex(
        (item) => item?.isDefaultPhone === true
      );

      const formattedContact = contactList?.map((ele, index) => {
        if (ele?.isDefaultPhone) {
          return { isDefaultPhone: true };
        }
        return {
          isDefaultPhone: false,
          id: ele?.otherData?.id > 0 ? ele?.otherData?.id?.toString() : "new",
          contact_order: ele?.otherData?.contact_order
            ? ele?.otherData?.contact_order * 10
            : null,
          first_name: getFirstAndLastName(ele?.name, "firstName"),
          last_name: getFirstAndLastName(ele?.name, "lastName"),
          has_keys: true,
          is_delete: ele?.is_delete ? ele?.is_delete : false,
          relation_to_user: ele?.relation?.relation_id?.toString(),
          call_before_911: index > indexOfDefaultContact ? false : true,
          primary_phone_id: ele?.otherData?.primary_phone_id
            ? ele?.otherData?.primary_phone_id?.toString()
            : "new",
          primary_number: ele?.phone,
          primary_phone_type:
            ele?.primary_phone_type?.phone_type_id?.toString(),
          primary_okay_to_text: false,
          primary_delete: false,
          secondary_phone_id: "",
          secondary_number: "",
          secondary_phone_type: "",
          secondary_okay_to_text: false,
          secondary_delete: false,
        };
      });
      let contactDataToSend = formattedContact?.filter(
        (item) => item?.isDefaultPhone !== true
      );
      contactDataToSend = contactDataToSend?.map((item, index) => {
        return {
          ...item,
          contact_order: (index + 1) * 10,
        };
      });
      let payload = {
        emergency_contract_id:
          selectedMedicalUser?.service_contract_id?.toString(),
        cif_signature: "cif_signature",
        lock_box_code: emergencyDetailState?.lockBoxCode
          ? emergencyDetailState?.lockBoxCode
          : undefined,
        lock_box_code_change: 0,
        lock_box_location: emergencyDetailState?.boxLocation,
        gate_code: emergencyDetailState?.gateInstruction,
        home_security_alarm_code: 0,
        add_other_access_info: "",
        access_to_residence: emergencyDetailState?.accessToResidence,
        location_of_vial_of_life: emergencyDetailState?.lifeLocation,
        primary_language:
          emergencyDetailState?.language?.language_id > 0
            ? emergencyDetailState?.language?.language_id?.toString()
            : getLanguageId(languages, emergencyDetailState?.language),
        township_borough: emergencyDetailState?.township,
        jurisdiction_info: emergencyDetailState?.jurisdiction,
        cross_street: emergencyDetailState?.street,
        special_instruction: emergencyDetailState?.spacialInstruction,
        hidden_key: emergencyDetailState?.keyLocation,
        emergency_contacts: contactDataToSend,
      };
      updateEmergencyPlan(
        dispatch,
        payload,
        onApiResponse,
        history,
        setPopUpMode
      );
    }
  };

  const handleFieldChange = (key, value) => {
    setEmergencyPlanState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const onEditContact = (item) => {
    setSelectedPhoneType(dispatch, item?.primary_phone_type);
    setEditingContact(item);
    setShowAddContactModal(true);
  };

  const onAddContact = () => {
    setEditingContact(null);
    setSelectedPhoneType(dispatch, null);
    setShowAddContactModal(true);
  };

  const handleAddContactModalClose = () => {
    setShowAddContactModal(false);
    setEditingContact(null);
  };

  const onSaveContact = (phoneInfo) => {
    // Safety check: prevent modifying default emergency contact
    if (editingContact?.isDefaultPhone) {
      onApiResponse("Cannot modify default emergency contact", "error");
      return;
    }

    if (editingContact) {
      // Edit existing contact - match mobile app logic exactly
      const phoneId = editingContact?.key;
      if (phoneId || phoneId === 0) {
        const selectedPhoneId = contactList?.findIndex(
          (item) => item?.key === phoneId
        );
        if (selectedPhoneId > -1) {
          let selectedPhone = contactList[selectedPhoneId];
          selectedPhone = {
            ...selectedPhone,
            phone: phoneInfo?.phoneNumber,
            phoneNumber: modifyTheContent(
              selectedPhone?.phoneNumber,
              phoneInfo?.contactName,
              phoneInfo?.relation?.relation,
              phoneInfo?.phoneNumber
            ),
            name: phoneInfo?.contactName,
            relation: phoneInfo?.relation,
            primary_phone_type: selectedPhoneType,
          };
          contactList[selectedPhoneId] = selectedPhone;
          setContactList([...contactList]); // Trigger re-render
        }
      }
    } else {
      // Add new contact - match mobile app logic exactly
      let list = JSON.parse(JSON.stringify(contactList));
      let obj = {
        key: contactList?.length,
        phoneNumber: phoneInfo?.contactName + ", " + phoneInfo?.relation?.relation + "\n" + phoneInfo?.phoneNumber,
        name: phoneInfo?.contactName,
        phone: phoneInfo?.phoneNumber,
        relation: phoneInfo?.relation,
        primary_phone_type: selectedPhoneType,
      };
      if (obj?.primary_phone_type?.phone_type_code === "ECV") {
        const indexOfDefaultContact = list?.findIndex(
          (item) => item?.isDefaultPhone === true
        );
        list?.splice(indexOfDefaultContact, 0, obj);
        setContactList(list);
      } else {
        list.push(obj);
        setContactList(list);
      }
    }

    handleAddContactModalClose();
  };

  const onDeleteContact = (item) => {
    setPopUpMode({ mode: "deleteContact", item: item });
  };

  const handleResidanceFieldChange = (type, value) => {
    if (type === "isLockBox" && value === true) {
      const isOriginalCode =
        emergencyDetailState?.lockBoxCode ===
          emergencyData?.emergency_access?.lock_box_code
          ? true
          : false;
      setEmergencyDetailState((prev) => {
        return {
          ...prev,
          [type]: value,
          ...(isOriginalCode ? { lockBoxCode: "" } : {}),
        };
      });
    } else if (type === "lockBoxCode") {
      if (value?.length <= 5) {
        setEmergencyDetailState((prev) => {
          return { ...prev, [type]: value };
        });
      }
    } else {
      setEmergencyDetailState((prev) => {
        return { ...prev, [type]: value };
      });
    }
  };

  const onLockBoxIconClick = () => {
    if (emergencyDetailState?.isLockBox) {
      setEmergencyDetailState((prev) => {
        return {
          ...prev,
          isShowLockBoxCode: !prev?.isShowLockBoxCode,
          showPasswordIcon: !prev?.showPasswordIcon,
        };
      });
    } else {
      showModal(titles?.lock_box_code_security);
    }
  };

  const handleDropdownSelect = (selectedOption) => {
    setEmergencyDetailState((prev) => {
      return { ...prev, language: selectedOption };
    });
  };

  const modifyTheContent = (data, nameUpdate, relationUpdate, phoneUpdate) => {
    let inputString = data;
    let parts = inputString.split(",");
    let firstPart = parts[0];
    let secondPart = parts[1].split("\n")[0];
    let thirdPart = parts[1].split("\n")[1];

    if (nameUpdate) {
      firstPart = nameUpdate;
    }
    if (relationUpdate) {
      secondPart = relationUpdate;
    }
    if (phoneUpdate) {
      thirdPart = phoneUpdate;
    }
    let concludedString = firstPart + ", " + secondPart + "\n" + thirdPart;
    return concludedString;
  };


  const showModal = (description) => {
    setInfoModalPopup({
      description,
      isVisible: true,
    });
  };

  const protected_address = medicalUserDetail?.protected_address;
  return (
    <MainLayout
      text={titles?.emergency_plan}
      isBackHide={false}
      isNavbar={true}
      isLoading={loading}
      white
    >
      <ChangesSavedModal
        show={popupMode?.mode === "changesSaved"}
        onClose={() => setPopUpMode({ mode: "" })}
      />
      <InformationModal
        show={infoModalPopup?.isVisible}
        setShow={setInfoModalPopup}
        infoText={infoModalPopup?.description}
      />

      {/* Snackbar - always sticky below header (no animation) */}
      {snackbarState?.show && (
        <div className="snackbar_sticky">
          <MessageSnackBar
            message={snackbarState?.message}
            isVisible={true}
            type={snackbarState?.type}
            autoHideDuration={snackbarState?.autoHideDuration}
            onDismiss={dismissSnackbar}
          />
        </div>
      )}
      <ConfirmationModal
        show={popupMode?.mode === "deleteContact"}
        setShow={setPopUpMode}
        svgIcon={<IconTrash color="#000000" />}
        title={titles?.delete_contact}
        description={titles?.are_you_sure_to_remove_number}
        btnText={titles?.remove}
        onClick={() => onRemoveContact()}
      />
      <div className="ep_container">
        {/* Subscriber Information Card */}
        <SubscriberInformationCard medicalUserDetail={medicalUserDetail} />

        {/* Subscriber Details Card */}
        <SubscriberDetailsCard
          medicalUserDetail={medicalUserDetail}
          protected_address={protected_address}
          onEmergencyInfoModal={() => showModal(titles?.emergency_info_tooltip)}
          showEditButton={false}
        />
        {/* Emergency Contacts Section */}
        <div className="ep_contacts_section">
          <div className="ep_section_header">
            <h3 className="ep_section_title">{titles?.emergency_contacts}</h3>
            <IconQuestion
              className="ep_help_icon"
              onClick={() =>
                showModal(titles?.modal_texts?.emergency_contact_help_tip)
              }
            />
          </div>

          <div className="ep_contacts_content">
            <p className="ep_description_text new_line_text mb18">{titles?.emergency_contacts_desc}</p>
            <p className="ep_description_text mb20">
              {titles?.emergency_contacts_second_desc}
            </p>

            <CustomDraggableCards
              initialItems={contactList}
              className="ep_contacts_list"
              onEdit={onEditContact}
              onDelete={onDeleteContact}
              onDragEnd={setContactList}
              emergencyData={emergencyData}
              showAddContactModal={showAddContactModal}
              onAddContact={onAddContact}
              titles={titles}
            />

            {showAddContactModal && (
              <div className="ep_inline_contact_form">
                <AddEmergencyContactModal
                  selectedContact={editingContact}
                  onSave={onSaveContact}
                  onClosePopup={handleAddContactModalClose}
                  onDeleteContact={onDeleteContact}
                  onApiResponse={onApiResponse}
                />
              </div>
            )}
          </div>
        </div>
        {/* Residence Details Section */}
        <div className="ep_residence_section">
          <h3 className="ep_section_title mb30">{titles?.residence_details}</h3>

          <div className="ep_form_fields">
            <CustomInput
              label={titles?.township_borough}
              labelClassName="ep_field_label"
              type="text"
              className="ep_input_field"
              placeholder={titles?.emergency_textfield_desc}
              required
              value={emergencyDetailState?.township}
              onChange={(e) =>
                handleResidanceFieldChange("township", e?.target?.value)
              }
            />
            <CustomInput
              label={titles?.jurisdiction}
              labelClassName="ep_field_label"
              type="text"
              className="ep_input_field"
              placeholder={titles?.emergency_textfield_desc}
              required
              value={emergencyDetailState?.jurisdiction}
              onChange={(e) =>
                handleResidanceFieldChange("jurisdiction", e?.target?.value)
              }
            />
            <CustomInput
              label={titles?.cross_street}
              labelClassName="ep_field_label"
              type="text"
              className="ep_input_field"
              placeholder={titles?.emergency_textfield_desc}
              required
              value={emergencyDetailState?.street}
              onChange={(e) => handleResidanceFieldChange("street", e?.target?.value)}
            />
            <div className="ep_char_limit_field">
              <CustomInput
                label={titles?.special_instructions}
                labelClassName="ep_field_label"
                type="text"
                className="ep_input_field"
                multiline={true}
                placeholder={titles?.tell_us_details_should_know}
                value={emergencyDetailState?.spacialInstruction}
                onChange={(e) =>
                  handleResidanceFieldChange("spacialInstruction", e?.target?.value)
                }
                maxLength={50}
              />
              <div className="ep_char_count">
                {50 - (emergencyDetailState?.spacialInstruction?.length || 0)} characters remaining
              </div>
            </div>
            <div className="ep_language_field">
              <p className="ep_field_label">{titles?.spoken_language_inside_home}</p>
              <CustomSelectBox
                required={true}
                options={languages?.map((ele) => {
                  return { label: ele?.language, value: ele?.language };
                })}
                selectedValue={
                  emergencyDetailState?.language?.length > 0
                    ? emergencyDetailState?.language
                    : emergencyDetailState?.language?.language
                }
                title={titles?.spoken_language_inside_home}
                onChange={(item) => handleDropdownSelect(item?.value)}
                className="ep_select_field"
              />
            </div>
          </div>
        </div>
        {/* Residence Entry Section */}
        <div className="ep_entry_card">
          <div className="ep_section_header">
            <h3 className="ep_section_title">{titles?.residence_entry}</h3>
            <IconQuestion
              className="ep_help_icon"
              onClick={() =>
                showModal(titles?.modal_texts?.residence_entry_help_tip)
              }
            />
          </div>

          <div className="ep_entry_content">
            <p className="ep_description_text">
              {titles?.please_fill_out_applies_subscriber}
            </p>

            <div className="ep_entry_fields">
              <div className="ep_char_limit_field">
                <CustomInput
                  label={titles?.gate_instructions}
                  labelClassName="ep_field_label"
                  type="text"
                  className="ep_input_field"
                  placeholder={titles?.tell_us_pass_codes_get_through}
                  multiline={true}
                  value={emergencyDetailState?.gateInstruction}
                  onChange={(e) =>
                    handleResidanceFieldChange("gateInstruction", e?.target?.value)
                  }
                  maxLength={50}
                />
                <div className="ep_char_count">
                  {50 - (emergencyDetailState?.gateInstruction?.length || 0)} characters remaining
                </div>
              </div>
              <div className="ep_char_limit_field">
                <CustomInput
                  label={titles?.access_to_residence}
                  labelClassName="ep_field_label"
                  type="text"
                  className="ep_input_field"
                  placeholder={titles?.tell_us_any_details}
                  multiline={true}
                  value={emergencyDetailState?.accessToResidence}
                  onChange={(e) =>
                    handleResidanceFieldChange("accessToResidence", e?.target?.value)
                  }
                  maxLength={50}
                />
                <div className="ep_char_count">
                  {50 - (emergencyDetailState?.accessToResidence?.length || 0)} characters remaining
                </div>
              </div>

              <div className="ep_lock_box_section">
                <div className="ep_lock_box_header">
                  <p className="ep_section_title">{titles?.lock_box_code}</p>
                  <IconQuestion
                    className="ep_help_icon"
                    onClick={() => showModal(titles?.modal_texts?.logbox_help_tip)}
                  />
                </div>

                <div className="ep_lock_box_content">
                  <div className="ep_radio_container">
                    <CustomCheckbox
                      className="ep_checkbox"
                      checked={emergencyDetailState?.isLockBox}
                      onChange={(e) => {
                        handleResidanceFieldChange(
                          "isLockBox",
                          e.target.checked
                        );
                      }}
                    />
                    <span className="ep_radio_label">{titles?.update_code}</span>
                  </div>

                  <div className="ep_lock_box_input_container">
                    <input
                      maxLength={4}
                      disabled={!emergencyDetailState?.isLockBox ? true : false}
                      className="ci_input lock_box_code_input"
                      type={emergencyDetailState?.showPasswordIcon || !emergencyDetailState?.isLockBox ? "password" : "text"}
                      value={emergencyDetailState["lockBoxCode"]}
                      onChange={(e) => {
                        handleResidanceFieldChange("lockBoxCode", e?.target?.value);
                      }}
                    />
                    {emergencyDetailState?.isLockBox ? (
                      emergencyDetailState?.showPasswordIcon ? (
                        <IconReveal
                          className={"ci_iconHide"}
                          onClick={() => onLockBoxIconClick()}
                        />
                      ) : (
                        <IconHide
                          className={"ci_iconHide"}
                          onClick={() => onLockBoxIconClick()}
                        />
                      )
                    ) : (
                      <IconQuestion
                        className={"lock_input_note_icon"}
                        onClick={() => onLockBoxIconClick()}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="ep_char_limit_field">
                <CustomInput
                  label={titles?.lock_box_location}
                  labelClassName="ep_field_label"
                  type="text"
                  className="ep_input_field"
                  placeholder={titles?.tell_where_find_it}
                  multiline={true}
                  value={emergencyDetailState?.boxLocation}
                  onChange={(e) => {
                    handleResidanceFieldChange("boxLocation", e?.target?.value);
                  }}
                  maxLength={50}
                />
                <div className="ep_char_count">
                  {50 - (emergencyDetailState?.boxLocation?.length || 0)} characters remaining
                </div>
              </div>
              <div className="ep_char_limit_field">
                <CustomInput
                  label={titles?.hidden_key_location}
                  labelClassName="ep_field_label"
                  type="text"
                  className="ep_input_field"
                  placeholder={titles?.tell_where_find_it}
                  multiline={true}
                  value={emergencyDetailState?.keyLocation}
                  onChange={(e) => {
                    handleResidanceFieldChange("keyLocation", e?.target?.value);
                  }}
                  maxLength={50}
                />
                <div className="ep_char_count">
                  {50 - (emergencyDetailState?.keyLocation?.length || 0)} characters remaining
                </div>
              </div>
              <div className="ep_char_limit_field">
                <CustomInput
                  label={titles?.vial_life_location}
                  labelClassName="ep_field_label"
                  type="text"
                  className="ep_input_field"
                  placeholder={titles?.tell_where_find_it}
                  multiline={true}
                  value={emergencyDetailState?.lifeLocation}
                  onChange={(e) => {
                    handleResidanceFieldChange("lifeLocation", e?.target?.value);
                  }}
                  maxLength={50}
                />
                <div className="ep_char_count">
                  {50 - (emergencyDetailState?.lifeLocation?.length || 0)} characters remaining
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Digital Signature Section */}
        <div className="ep_signature_section">
          <h3 className="ep_section_title">{titles?.digital_signature}</h3>

          <div className="ep_signature_content">
            <p className="ep_description_text">{titles?.please_read_the_following}</p>

            <div className="ep_consent_box">
              <p className="ep_description_text">
                {titles?.emergency_signature_desc}
              </p>
            </div>

            <p className="ep_description_text">{titles?.verify_your_signature}</p>

            <div className="ep_signature_confirmation">
              <CustomCheckbox
                className="ep_checkbox"
                checked={emergencyPlanState?.isAcceptSignature}
                onChange={() => {
                  handleFieldChange(
                    "isAcceptSignature",
                    !emergencyPlanState?.isAcceptSignature
                  );
                }}
              />
              <div className="ep_signature_text_container">
                <p className="ep_signature_text">{titles?.accept}</p>
                {/* <p className="ep_signature_text">{`**${
                  accountHolderData?.firstname + " " + accountHolderData?.lastname
                }\nc/o ${
                  medicalUserDetail?.firstname + " " + medicalUserDetail?.lastname
                }**`}</p> */}
              </div>
            </div>

            <CustomButton
              disabled={!emergencyPlanState?.isAcceptSignature || showAddContactModal}
              width="100%"
              height="50px"
              className="ep_submit_button"
              text={titles?.submit}
              onClick={() => onAccept()}
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EmergencyPlan;
